import { useMemo, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { invoiceExampleCatalog } from "./catalog";
import {
  formatCandidateClient,
  formatCandidateEvent,
  formatCandidateInvoiceNumber,
  formatImportedAt,
  resolveImportDisplayStatus,
  summarizeImportQueue,
} from "./importReviewStatus";
import { useInvoiceImport } from "./InvoiceImportProvider";
import { importDisplayStatusLabels } from "./types";

const QUEUE_PAGE_SIZE = 50;
type QueueView = "active" | "imported";

export function InvoiceImportBatchPage() {
  const {
    queueItems,
    queueLoading,
    catalogSelection,
    isBatchParsing,
    batchParseTotal,
    batchParseCompleted,
    pendingUploadCount,
    toggleCatalogSelection,
    selectAllCatalog,
    clearCatalogSelection,
    addUploads,
    parseSelected,
    isCatalogImported,
  } = useInvoiceImport();

  function importedEventName(item: (typeof queueItems)[number]): string {
    return item.draft?.projectName?.trim() || item.draft?.clientName?.trim() || "Imported Event";
  }

  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [queuePage, setQueuePage] = useState(0);
  const [queueView, setQueueView] = useState<QueueView>("active");

  const activeQueueItems = useMemo(
    () => queueItems.filter((item) => !item.importedEventId),
    [queueItems],
  );
  const importedQueueItems = useMemo(
    () => queueItems.filter((item) => item.importedEventId),
    [queueItems],
  );
  const visibleQueueItems = queueView === "imported" ? importedQueueItems : activeQueueItems;

  const summary = useMemo(() => summarizeImportQueue(activeQueueItems), [activeQueueItems]);
  const sortedQueue = useMemo(
    () => [...visibleQueueItems].sort((left, right) => left.source.filename.localeCompare(right.source.filename)),
    [visibleQueueItems],
  );
  const visibleQueue = sortedQueue.slice(0, (queuePage + 1) * QUEUE_PAGE_SIZE);
  const canLoadMoreQueue = visibleQueue.length < sortedQueue.length;

  const parseableCount = useMemo(() => {
    let count = 0;
    for (const filename of catalogSelection) {
      if (!isCatalogImported(filename)) count += 1;
    }
    count += pendingUploadCount;
    for (const item of queueItems) {
      if (item.importedEventId) continue;
      if (item.parseStatus === "failed") count += 1;
    }
    return count;
  }, [catalogSelection, isCatalogImported, pendingUploadCount, queueItems]);

  function onUploadInput(event: ChangeEvent<HTMLInputElement>) {
    if (event.target.files) addUploads(event.target.files);
    event.target.value = "";
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragActive(false);
    if (event.dataTransfer.files?.length) addUploads(event.dataTransfer.files);
  }

  return (
    <section className="invoice-import-page">
      <div className="desktop-view">
        <div className="invoice-import-head">
          <div>
            <Link className="text-button" to="/events">← Events</Link>
            <h1 className="invoice-import-title">Import invoices</h1>
            <p className="invoice-import-lead">
              Upload HoneyBook invoices to create Events. Review the details before saving.
            </p>
          </div>
        </div>
      </div>

      <section className="mobile-view invoice-import-mobile">
        <Link className="text-button" to="/events">← Events</Link>
        <h1 className="invoice-import-title">Import invoices</h1>
        <p className="invoice-import-lead">Upload HoneyBook invoices to create Events. Review the details before saving.</p>
      </section>

      <div className="invoice-import-upload">
        <input
          ref={uploadInputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          onChange={onUploadInput}
        />
        <div
          className={`invoice-import-dropzone${dragActive ? " is-active" : ""}`}
          onDragOver={(event) => { event.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
        >
          <p><strong>Add invoices</strong> · Drop PDF invoices here or choose files</p>
          <button type="button" className="secondary-button" onClick={() => uploadInputRef.current?.click()}>
            Choose files
          </button>
        </div>
      </div>

      <div className="invoice-import-section">
        <div className="section-heading small">
          <div>
            <h2>Available invoices</h2>
            <p>Select invoices to add to the queue.</p>
          </div>
          <div className="invoice-import-toolbar">
            <button type="button" className="text-button" onClick={selectAllCatalog}>Select all</button>
            <button type="button" className="text-button" onClick={clearCatalogSelection}>Clear</button>
          </div>
        </div>

        <div className="invoice-import-catalog">
          {invoiceExampleCatalog.map((entry) => {
            const queueItem = queueItems.find((item) => item.source.filename === entry.filename);
            const displayStatus = queueItem ? resolveImportDisplayStatus(queueItem) : null;
            const imported = isCatalogImported(entry.filename);
            return (
              <label key={entry.filename} className={`invoice-import-catalog-row${imported ? " is-imported" : ""}`}>
                <input
                  type="checkbox"
                  checked={catalogSelection.has(entry.filename)}
                  disabled={imported}
                  onChange={() => toggleCatalogSelection(entry.filename)}
                />
                <span className="invoice-import-catalog-name">{entry.filename}</span>
                {displayStatus && (
                  <span className={`import-status-pill import-status-${displayStatus}`}>
                    {importDisplayStatusLabels[displayStatus]}
                  </span>
                )}
                {imported && queueItem?.importedEventId && (
                  <Link className="text-button" to={`/events/${queueItem.importedEventId}`}>Open Event</Link>
                )}
              </label>
            );
          })}
        </div>

        <div className="invoice-import-panel">
          <button
            type="button"
            className="primary-button"
            disabled={isBatchParsing || parseableCount === 0}
            onClick={() => void parseSelected()}
          >
            {isBatchParsing
              ? "Processing…"
              : `Add to queue (${parseableCount})`}
          </button>
          {isBatchParsing && (
            <p className="invoice-import-status">Processing…</p>
          )}
        </div>
      </div>

      <div className="invoice-import-section">
        <div className="section-heading small">
          <div>
            <h2>Import queue</h2>
            <p>
              {queueView === "active"
                ? `${activeQueueItems.length} active`
                : `${importedQueueItems.length} imported`}
              {queueView === "active" && activeQueueItems.length > 0 && (
                <>
                  {" · "}
                  {summary.ready} ready · {summary.needs_review} need review · {summary.failed} failed
                </>
              )}
            </p>
          </div>
          <div className="invoice-import-toolbar invoice-import-queue-tabs">
            <button
              type="button"
              className={`text-button${queueView === "active" ? " is-active" : ""}`}
              onClick={() => { setQueueView("active"); setQueuePage(0); }}
            >
              Active
            </button>
            <button
              type="button"
              className={`text-button${queueView === "imported" ? " is-active" : ""}`}
              onClick={() => { setQueueView("imported"); setQueuePage(0); }}
            >
              Imported{importedQueueItems.length > 0 ? ` (${importedQueueItems.length})` : ""}
            </button>
          </div>
        </div>

        {queueLoading ? (
          <p className="muted invoice-import-empty">Loading import queue…</p>
        ) : sortedQueue.length === 0 ? (
          <p className="muted invoice-import-empty">
            {queueView === "active"
              ? "No invoices queued. Choose invoices above to get started."
              : "Imported invoices appear here after you create an Event from review."}
          </p>
        ) : (
          <>
            <div className="desktop-view invoice-import-queue-table-wrap">
              <table className="invoice-import-queue-table">
                <thead>
                  <tr>
                    <th>Filename</th>
                    {queueView === "imported" ? (
                      <>
                        <th>Invoice #</th>
                        <th>Event</th>
                        <th>Imported</th>
                        <th>Action</th>
                      </>
                    ) : (
                      <>
                        <th>Client</th>
                        <th>Event</th>
                        <th>Invoice #</th>
                        <th>Status</th>
                        <th>Action</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {visibleQueue.map((item) => {
                    const status = resolveImportDisplayStatus(item);
                    return (
                      <tr key={item.id}>
                        <td>{item.source.filename}</td>
                        {queueView === "imported" ? (
                          <>
                            <td>{formatCandidateInvoiceNumber(item.draft)}</td>
                            <td>{importedEventName(item)}</td>
                            <td>{formatImportedAt(item.importedAt)}</td>
                            <td>
                              {item.importedEventId && (
                                <Link className="text-button" to={`/events/${item.importedEventId}`}>Open Event</Link>
                              )}
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{formatCandidateClient(item.draft)}</td>
                            <td>{formatCandidateEvent(item.draft)}</td>
                            <td>{formatCandidateInvoiceNumber(item.draft)}</td>
                            <td>
                              <span className={`import-status-pill import-status-${status}`}>
                                {importDisplayStatusLabels[status]}
                              </span>
                              {item.error && <small className="invoice-import-queue-error">{item.error}</small>}
                            </td>
                            <td>
                              {item.importedEventId ? (
                                <Link className="text-button" to={`/events/${item.importedEventId}`}>Open Event</Link>
                              ) : item.parseStatus === "parsed" ? (
                                <Link className="text-button" to={`/events/import/review/${encodeURIComponent(item.id)}`}>Review</Link>
                              ) : item.parseStatus === "failed" ? (
                                <span className="muted">Re-parse batch</span>
                              ) : (
                                <span className="muted">—</span>
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mobile-view invoice-import-queue-cards">
              {visibleQueue.map((item) => {
                const status = resolveImportDisplayStatus(item);
                return (
                  <article key={item.id} className="invoice-import-queue-card">
                    <strong>{item.source.filename}</strong>
                    {queueView === "imported" ? (
                      <>
                        <p>Invoice {formatCandidateInvoiceNumber(item.draft)} · {importedEventName(item)}</p>
                        <p>Imported {formatImportedAt(item.importedAt)}</p>
                      </>
                    ) : (
                      <>
                        <p>{formatCandidateClient(item.draft)} · {formatCandidateEvent(item.draft)}</p>
                        <p>Invoice {formatCandidateInvoiceNumber(item.draft)}</p>
                        <span className={`import-status-pill import-status-${status}`}>
                          {importDisplayStatusLabels[status]}
                        </span>
                      </>
                    )}
                    {item.error && <small className="invoice-import-queue-error">{item.error}</small>}
                    {item.importedEventId ? (
                      <Link className="primary-button invoice-import-review-link" to={`/events/${item.importedEventId}`}>
                        Open Event
                      </Link>
                    ) : item.parseStatus === "parsed" && (
                      <Link className="primary-button invoice-import-review-link" to={`/events/import/review/${encodeURIComponent(item.id)}`}>
                        Review
                      </Link>
                    )}
                  </article>
                );
              })}
            </div>

            {canLoadMoreQueue && (
              <button type="button" className="secondary-button invoice-import-load-more" onClick={() => setQueuePage((page) => page + 1)}>
                Show more ({sortedQueue.length - visibleQueue.length} remaining)
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
